import { Account, Latitude, Longitude, Timestamp, Uid, WorkingHours, WorkingHoursOverrides } from './core';
import { MemberLiveBadge } from './memberLiveBadge';
import { DAY, HOUR, MINUTE } from "../utils/time";
import { ANDROID_BUILD, CSV_EXTENSION, EXCEL_EXTENSION, IOS_BUILD } from '../constants/app';
import { TRUNCATE_LENGTH_DEFAULT, truncateText } from '../utils/strings';

export const MOVING_STATE_IDLING = "idling";
export const MOVING_STATE_HALTED = "halted";
export const MOVING_STATE_OFFDUTY = "offduty";

export const DEVICE_MODE_FOREGROUNDED = 1;
export const DEVICE_MODE_BACKGROUNDED = 2;
export const DEVICE_MODE_SUSPENDED = 3;

export interface Member extends Account {
    id: Uid;
    battery_level: number;
    battery_plugged: boolean;
    unit_date: string;
    device: Device;
    google_calendar: boolean;
    unit_distance: string;
    role: Role;
    route_end_lat: Latitude;
    route_end_lng: Longitude;
    route_start_lat: Latitude;
    route_start_lng: Longitude;
    route_start_time: number;
    states: States;
    status_label: string;
    teams: number[];
    /** team whose hours and route start apply; 0 or absent = none (older servers don't send it) */
    primary_team?: number;
    unit_time: string;
    timestamps: Timestamps;
    travel_mode: string;
    username: string;
    vehicle_capacity: number;
    working_hours: WorkingHours;
    working_hours_overrides?: WorkingHoursOverrides;
    /** the person's own lunch break, "1200-1300", or "none" whatever the team's; missing to follow the team */
    lunch_break?: string;
    /** the lunch break the person takes, their own or their team's; missing for none */
    lunch?: { start: number; end: number; source: "individual" | "team" };
    twofactor_auth_enabled: boolean;
    twofactor_auth_method?: "" | "legacy" | "totp";
    has_password_login?: boolean;
    color: string;
    skills?: string[];
    expertise?: string;
    /** Statistics board layout (JSON string of BoardLayout), see components/statistics/registry/layout.ts */
    stats_layout?: string | object;
    /** the saved filter views of the web dashboard, as JSON */
    filter_views?: string | object;
    // client-side only: ts of the last location with moving speed (see hasMovingSpeed), carried forward in updateMultiMemberState
    lastMovingTs?: Timestamp;
}

export type Role = "worker" | "operator" | "admin";

export interface MemberListItem extends Member {
    selected: boolean;
}

export interface Device {
    model: string;
    manufacturer: "Apple" | "Google" | "Samsung" | string;
    os: string;
    app_launches: number;
    app_version: number;
    app_version_name: string;
    id: string;
    mode: number;
    diagnostics_path: string;
    name?: string;
    /** iOS CLAuthorizationStatus as the app reports it, e.g. "4" or "__C.CLAuthorizationStatus(rawValue: 4)" */
    permission_ios_authorization_status?: string;
}

export interface Timestamps {
    created: Timestamp;
    login: Timestamp;
    sync: Timestamp;
    tracking: Timestamp;
    suspended?: Timestamp;
    recovered?: Timestamp;
    /** first invite since the member was created or reactivated; 0 = not recorded (invited before Sep 2026) */
    invited?: Timestamp;
    /** latest invite, resends included */
    reminded?: Timestamp;
}

export interface States {
    moving: string;
    tracking_status: boolean;
    location_services: boolean;
    location_permission: boolean;
    background_location?: boolean;
    login_status: boolean;
    battery_save: boolean;
    off_trackrecording: boolean;
    off_gps_stationary: boolean;
    ignoring_battery_optimization: boolean;
}

export type ImportMemberFileFormat = typeof CSV_EXTENSION | typeof EXCEL_EXTENSION;

export function isAdmin(role: string) {
    return role === 'admin';
}

export function isOperator(role: string) {
    return role === 'operator';
}

export function isWorker(role: string) {
    return role === 'worker';
}

export function isAdminOrOperator(member: Member) {
    return isAdmin(member.role) || isOperator(member.role);
}

export function isIdling(member: Member) {
    return member.states && member.states.moving === MOVING_STATE_IDLING
}

export function isHalted(member: Member) {
    return member.states && member.states.moving === MOVING_STATE_HALTED
}

// speed is integer km/h; jumping GPS routinely reports a few km/h while standing
// still, so require the same > 3 km/h the server uses in Account.getMovingState
export function hasMovingSpeed(member: Member) {
    return !!member.location && member.location.speed > ON_THE_WAY_MIN_SPEED_KMH;
}

export function isOnTheWay(member: Member) {
    if (!member.location || !hasRecentGps(member, 10 * MINUTE)) {
        return false;
    }
    // the server already classifies stationary/paused members; never override that
    if (isIdling(member) || isHalted(member)) {
        return false;
    }
    if (hasMovingSpeed(member)) {
        return true;
    }
    // GPS points routinely report speed 0 mid-drive (stops, noise); hold the state for a grace window
    return !!member.lastMovingTs && Date.now() - member.lastMovingTs < ON_THE_WAY_GRACE;
}

const ON_THE_WAY_MIN_SPEED_KMH = 3;
const ON_THE_WAY_GRACE = 2 * MINUTE;

export function hasRecentGps(member: Member, millis: number = 18 * HOUR): boolean {
    return member.location && Date.now() - member.location.ts < millis;
}

export function isOffDuty(member: Member) {
    return member.states.moving === MOVING_STATE_OFFDUTY;
}

export function isInService(member: Member) {
    return member.states.login_status
        && member.states.tracking_status
        && !isOffDuty(member)
        && hasRecentGps(member, 2 * DAY)
}

export function isLoggedIn(member: Member) {
    return member.states.login_status;
}

export function isTrackingOn(member: Member) {
    return member.states.tracking_status;
}

export function hasValidDevice(member?: Member) {
    return (member?.device ?? false) && (!!member?.device?.id);
}

export type EffectiveStatus =
    | "admin-or-operator"
    | "invited"
    | "offduty"
    | "loggedout"
    | "trackingoff"
    | "locationservicesoff"
    | "on-the-way"
    | "idling"
    | "none";

// an idling-derived live badge (pause / at place) is position-based evidence the
// member is stationary; the speed-derived on-the-way state must never win over it
// (a single jumpy GPS fix carries a spurious speed the server takes at face value)
export function isStationaryPerLiveBadge(liveBadge: MemberLiveBadge | undefined) {
    return liveBadge?.kind === "pausing" || liveBadge?.kind === "place";
}

// Single source of truth for the one status a member is displayed as; badge
// renderers and status filters must both derive from this precedence chain.
// Pass the member's live badge (see getMemberLiveBadge) so the status agrees
// with the pause / at-place badge the row displays.
export function getEffectiveStatus(member: Member | undefined, showAllBadges = true, liveBadge?: MemberLiveBadge): EffectiveStatus {
    if (!member || (isAdminOrOperator(member) && !isIdling(member) && !isOnTheWay(member)) || !member.states) {
        return "admin-or-operator";
    }
    if (!hasValidDevice(member)) {
        return "invited";
    }
    if (isOffDuty(member)) {
        return "offduty";
    }
    if (showAllBadges && !isLoggedIn(member)) {
        return "loggedout";
    }
    if (showAllBadges && !isTrackingOn(member)) {
        return "trackingoff";
    }
    if (showAllBadges && !member.states.location_services) {
        return "locationservicesoff";
    }
    if (isStationaryPerLiveBadge(liveBadge)) {
        return "idling";
    }
    if (isOnTheWay(member)) {
        return "on-the-way";
    }
    if (isIdling(member)) {
        return "idling";
    }
    return "none";
}

export function hasAppleDevice(member?: Member) {
    return member?.device?.manufacturer === "Apple";
}

export function hasDeviceAnalysisFeature(member?: Member): boolean {
    if (member && hasValidDevice(member)) {
        return member.device.manufacturer === "Apple" && member.device.app_version >= 274
            || member.device.manufacturer !== "Apple" && member.device.app_version >= 245;
    }
    return false;
}

export const getTruncatedMemberName = (member: Member) => {
    return member.name && member.name.length > TRUNCATE_LENGTH_DEFAULT ? truncateText(member.name) : member.name;
}

export const isAppUpdateAvailable = (member?: Member) => {
    const buildNumber = member?.device?.app_version;
    return buildNumber ? (hasAppleDevice(member) ? buildNumber < IOS_BUILD : buildNumber < ANDROID_BUILD) : false;
}
